import { useState } from 'react';

import { HEALTH_COLORS, barHeight } from './constants';

import { PoolRow } from '@/services/preferredPoolsAPI';
import styled from '@/styles';

// ui-prototype/preferred-worker-pool/client-detail.html #barChart. One bar per
// pool — ALL pools, not just the chip subset (plan edge #21). The server
// returns pools ascending by active_member_count, which is the chart's
// required order, so no client-side sort.

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  position: 'relative',
});

const HintRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
  fontSize: '12px',
  color: '#5B5B5B',
  paddingBottom: 4,
});

const Chart = styled('div', {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  borderBottom: '1px solid #DADADA',
  height: 48,
});

const BarButton = styled('button', {
  all: 'unset',
  flex: 1,
  height: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  cursor: 'pointer',
});

const Bar = styled('div', {
  width: '100%',
  borderRadius: '4px 4px 0 0',
  opacity: 0.7,
  variants: {
    selected: {
      true: {
        opacity: 1,
        border: '2px solid #2E8722',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.12)',
      },
    },
  },
  '&:hover': { opacity: 1 },
});

const Tooltip = styled('div', {
  position: 'absolute',
  bottom: 56,
  transform: 'translateX(-50%)',
  background: '#FCFCFC',
  border: '1px solid #DADADA',
  boxShadow: '0 -2px 4px rgba(0,0,0,0.12)',
  borderRadius: 8,
  height: 32,
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '14px',
  color: '#1C1C1C',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 60,
});

const Dot = styled('span', {
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
});

type Props = {
  pools: PoolRow[];
  selectedPoolId: number | null;
  onSelect: (poolId: number) => void;
};

const PoolHealthChart = ({ pools, selectedPoolId, onSelect }: Props) => {
  const [hovered, setHovered] = useState<PoolRow | null>(null);
  const hoveredIndex = hovered
    ? pools.findIndex((pool) => pool.id === hovered.id)
    : -1;

  return (
    <Wrapper>
      <HintRow>Hover any bar to view more info</HintRow>
      <Chart data-testid="barChart">
        {pools.map((pool) => (
          <BarButton
            key={pool.id}
            aria-label={pool.skill.name ?? `Pool ${pool.id}`}
            data-health={pool.health}
            type="button"
            onClick={() => onSelect(pool.id)}
            onMouseEnter={() => setHovered(pool)}
            onMouseLeave={() => setHovered(null)}
          >
            <Bar
              selected={pool.id === selectedPoolId}
              style={{
                height: barHeight(pool.active_member_count),
                background: HEALTH_COLORS[pool.health],
              }}
            />
          </BarButton>
        ))}
      </Chart>
      {hovered && hoveredIndex >= 0 && (
        <Tooltip
          data-testid="barTooltip"
          style={{
            left: `${((hoveredIndex + 0.5) / pools.length) * 100}%`,
          }}
        >
          <Dot
            data-testid="barTooltipDot"
            style={{ background: HEALTH_COLORS[hovered.health] }}
          />
          <b data-testid="barTooltipName">{hovered.skill.name}</b>
          <span data-testid="barTooltipCount">
            ({hovered.active_member_count}/{hovered.target_depth})
          </span>
        </Tooltip>
      )}
    </Wrapper>
  );
};

export default PoolHealthChart;
